/**
 * API client for the Simpliloquy for Mark Worker.
 *
 * Production (Pages, .env.production sets VITE_WORKER_ORIGIN=""): every
 * /api/* and /ws/* fetch is same-origin against the Pages domain. Pages
 * Functions in web/functions/{api,ws}/ proxy server-side to the Worker.
 * Same-origin keeps the SameSite=None cookie first-party on iOS Safari;
 * cross-origin fetches there get the cookie stripped by ITP.
 *
 * Dev (npm run dev): VITE_WORKER_ORIGIN points at the workers.dev URL or
 * a local wrangler dev. The Vite dev server proxy in vite.config.ts
 * forwards /api/* server-side, so the browser sees same-origin too.
 */

const ORIGIN = (import.meta.env.VITE_WORKER_ORIGIN as string | undefined) ?? '';

function url(path: string): string {
  return `${ORIGIN}${path}`;
}

async function jfetch<T>(input: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(url(input), {
    ...init,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
  const body = (await res.json().catch(() => ({}))) as T & { ok?: boolean; error?: string };
  if (!res.ok || body.ok === false) {
    const err = body.error ?? `http_${res.status}`;
    throw new ApiError(err, res.status, body);
  }
  return body;
}

export class ApiError extends Error {
  constructor(public code: string, public status: number, public body: unknown) {
    super(code);
  }
}

// ----- types matching the Worker -----

export interface AuthStatus {
  ok: true;
  user: 'mark';
  device: string;
  expires_at: number;
}

export interface AgentEntry {
  slug: string;
  role: string;
  profile_id?: string;
}

export interface AgentPreviewStage {
  id: string;
  name: string;
  length: number;
}

export interface AgentPreview {
  ok: true;
  slug: string;
  constitution_sha256: string;
  payload_hash: string;
  assembled_at: string;
  stages: {
    identity: AgentPreviewStage;
    canonicals: AgentPreviewStage[];
    persona: AgentPreviewStage;
    boot: AgentPreviewStage;
    skills: AgentPreviewStage[];
  };
  composed_length: number;
  composed_preview: string;
  composed_full?: string;
}

export interface SessionRow {
  id: string;
  agent_active: string;
  device_label: string;
  opened_at: string;
  closed_at: string | null;
}

export interface SessionDetail {
  ok: true;
  manifest: {
    id: string;
    user_id: string;
    device_label: string;
    agent_active: string;
    opened_at: string;
    closed_at: string | null;
  };
  messages: MessageRow[];
}

export interface MessageRow {
  id: string;
  kind:
    | 'user_message'
    | 'agent_message'
    | 'dispatch_started'
    | 'dispatch_output'
    | 'dispatch_finished'
    | 'interactive_prompt'
    | 'authorize_proposal'
    | 'authorize_decision'
    | 'passweb_pass'
    | 'passweb_fail';
  agent_slug: string | null;
  dispatch_id: string | null;
  content: string | null;
  metadata: string | null;
  created_at: string;
}

// ----- calls -----

export const api = {
  health: () => jfetch<{ ok: true; service: string; version: string }>('/api/health'),

  authStatus: () => jfetch<AuthStatus>('/api/auth/status'),
  login: (secret: string, device_label: string) =>
    jfetch<AuthStatus>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ secret, device_label }),
    }),
  logout: () => jfetch<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  signOutEverywhere: () =>
    jfetch<{ ok: true; epoch: number }>('/api/auth/sign_out_everywhere', {
      method: 'POST',
    }),

  listAgents: () =>
    jfetch<{ ok: true; default_agent: string; agents: AgentEntry[] }>('/api/agents'),

  agentPreview: (slug: string) =>
    jfetch<AgentPreview>(`/api/agents/${encodeURIComponent(slug)}/preview`),

  listSessions: () => jfetch<{ ok: true; sessions: SessionRow[] }>('/api/sessions'),
  createSession: (agent_slug?: string, model?: string | null) =>
    jfetch<{ ok: true; session_id: string; agent_slug: string }>('/api/sessions', {
      method: 'POST',
      body: JSON.stringify({ agent_slug, model: model ?? undefined }),
    }),
  getSession: (id: string) => jfetch<SessionDetail>(`/api/sessions/${encodeURIComponent(id)}`),
  closeSession: (id: string) =>
    jfetch<{ ok: true }>(`/api/sessions/${encodeURIComponent(id)}/close`, { method: 'POST' }),
  postMessage: (id: string, text: string, signal?: AbortSignal) =>
    jfetch<{
      ok: true;
      target: 'cloud' | 'local';
      reason: string;
      user_message_id: string;
      agent_message_id: string;
      agent_slug: string;
      text: string;
      dispatch_id?: string;
      queued?: boolean;
      working_dir?: string;
    }>(`/api/sessions/${encodeURIComponent(id)}/messages`, {
      method: 'POST',
      body: JSON.stringify({ text }),
      signal,
    }),

  daemonStatus: () =>
    jfetch<{
      ok: true;
      online: boolean;
      online_since: string | null;
      last_seen: string | null;
      hello: Record<string, unknown> | null;
      queued: number;
    }>('/api/daemon/status'),

  cancelDispatch: (dispatch_id: string) =>
    jfetch<{ ok: true }>('/api/daemon/cancel', {
      method: 'POST',
      body: JSON.stringify({ dispatch_id }),
    }),

  authorize: (session_id: string, proposal_id: string, decision: 'accept' | 'discard') =>
    jfetch<{ ok: true; decision: 'accept' | 'discard'; result?: string }>(
      `/api/sessions/${encodeURIComponent(session_id)}/authorize/${encodeURIComponent(proposal_id)}`,
      {
        method: 'POST',
        body: JSON.stringify({ decision }),
      },
    ),
};

// ----- websocket: live console for a session -----

export interface ConsoleEvent {
  v: number;
  kind:
    | 'daemon_status'
    | 'dispatch_started'
    | 'dispatch_output'
    | 'dispatch_finished'
    | 'interactive_prompt'
    | 'session_closed'
    | 'pong'
    | 'hello';
  body: unknown;
  ts: string;
}

export function openConsoleStream(session_id: string, onEvent: (ev: ConsoleEvent) => void): {
  close: () => void;
} {
  // Choose the websocket origin: same as ORIGIN if set, otherwise current location.
  const httpOrigin = ORIGIN || window.location.origin;
  const wsOrigin = httpOrigin.replace(/^http/, 'ws');
  const ws = new WebSocket(`${wsOrigin}/ws/console?session_id=${encodeURIComponent(session_id)}`);
  ws.onmessage = (ev) => {
    try {
      const parsed = JSON.parse(ev.data) as ConsoleEvent;
      onEvent(parsed);
    } catch {
      // ignore non-json
    }
  };
  return {
    close: () => {
      try {
        ws.close();
      } catch {
        // ignore
      }
    },
  };
}
